
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import RichTextEditor from '../components/RichTextEditor';
import ScrollableDropdown from '../components/ScrollableDropdown';
import MediaViewer from '../components/MediaViewer';
import AttachmentUploader from '../components/AttachmentUploader';

const EditBug = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [applications, setApplications] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [errors, setErrors] = useState({});
  const [workType, setWorkType] = useState('bug');
  const isObservation = workType === 'observation';
  const isImprovement = workType === 'improvement';
  // Tracks whether the user has manually changed the workType after load.
  // We use a ref so it doesn't trigger re-renders.
  const userChangedWorkType = React.useRef(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    application: '',
    module: '',
    category: 'Functional',
    severity: 'Medium',
    priority: 'Medium',
    assignedTo: '',
    environment: '',
    domain: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [bugCode, setBugCode] = useState('');
  const [mongoId, setMongoId] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [domains, setDomains] = useState([]);

  useEffect(() => {
    fetchBug();
    fetchApplications();
    fetchDevelopers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch domains whenever the application changes (populated after fetchBug sets formData.application)
  useEffect(() => {
    if (!formData.application) {
      setDomains([]);
      return;
    }
    axios.get(`/api/applications/${formData.application}/domains`)
      .then(({ data }) => {
        const active = (data.data || []).filter(d => d.status === 'Active');
        setDomains(active);
      })
      .catch(err => {
        if (err.response?.status !== 403) console.error('Failed to fetch domains', err);
        setDomains([]);
      });
  }, [formData.application]);

  // Only reset category/severity/priority when the user MANUALLY changes the workType.
  // During initial data load (fetchBug), we do NOT want to overwrite the fetched values.
  useEffect(() => {
    if (!userChangedWorkType.current) return;
    setFormData((prev) => ({
      ...prev,
      category: isObservation ? 'Observation' : 'Functional',
      severity: isObservation ? 'Low' : 'Medium',
      priority: isObservation ? 'Low' : 'Medium',
    }));
  }, [isObservation]);

  const fetchBug = async () => {
    try {

      const isMongoId = /^[0-9a-fA-F]{24}$/.test(id);
      let bugData = null;

      if (isMongoId) {
        const { data } = await axios.get(`/api/bugs/${id}`);
        bugData = data.data;
      } else {
        const { data } = await axios.get(`/api/bugs?bugId=${id}&pageSize=10&includeArchived=true`);
        bugData = data.data.find(b => b.bugId?.toLowerCase() === id.toLowerCase());
      }

      if (!bugData) throw new Error('Bug not found');

      const bug = bugData;
      setBugCode(bug.bugId || '');
      setMongoId(bug._id || '');
      
      const derivedWorkType = bug.ticketType 
        ? bug.ticketType.toLowerCase() 
        : (bug.category === 'Observation' ? 'observation' : 'bug');
      // Set workType without flagging it as a user-initiated change.
      userChangedWorkType.current = false;
      setWorkType(derivedWorkType);
      const standardCategories = ['Functional', 'UI/UX', 'Performance', 'Security', 'Integration', 'Data', 'Observation'];
      const isOther = bug.category && !standardCategories.includes(bug.category);

      setFormData({
        title: bug.title || '',
        description: bug.description || '',
        application: bug.application?._id || '',
        module: bug.module || '',
        category: isOther ? 'Other' : (bug.category || 'Functional'),
        severity: bug.severity || 'Medium',
        priority: bug.priority || 'Medium',
        assignedTo: bug.assignedTo?._id || '',
        environment: bug.environment || '',
        domain: bug.domain?._id || bug.domain || ''
      });

      if (isOther) {
        setCustomCategory(bug.category);
      }

      const normalizedAttachments = (bug.attachments || []).map((att, index) => ({
        ...att,
        id: att.id || att._id || `existing-${index}`,
        url: att.url || (att.path ? (att.path.startsWith('/') ? att.path : `/${att.path}`) : '')
      }));
      setAttachments(normalizedAttachments);
      // Do NOT set isInitialized here — we use userChangedWorkType ref instead.
    } catch (error) {
      toast.error('Failed to fetch bug details');
      navigate('/bugs');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const { data } = await axios.get('/api/applications/for-bug-reporting');
      setApplications(data.data);
    } catch (error) {
      toast.error('Failed to load applications');
    }
  };

  const fetchDevelopers = async () => {
    try {
      const { data } = await axios.get('/api/auth/developers');
      setDevelopers(data.data);
    } catch (error) {
      console.error('Failed to fetch developers');
    }
  };

  const filteredDevelopers = useMemo(() => {
    if (!formData.application) return developers;
    const selectedApp = applications.find(app => app._id === formData.application);
    if (!selectedApp) return developers;

    const teamDevIds = new Set();
    if (selectedApp.developerId) teamDevIds.add(selectedApp.developerId._id || selectedApp.developerId);
    if (selectedApp.ownerId) teamDevIds.add(selectedApp.ownerId._id || selectedApp.ownerId);
    if (selectedApp.teamMembers) {
      selectedApp.teamMembers.forEach(member => {
        if (member.role === 'DEV' && member.userId) teamDevIds.add(member.userId._id || member.userId);
      });
    }

    if (teamDevIds.size === 0) return developers;
    return developers.filter(dev => teamDevIds.has(dev._id));
  }, [applications, developers, formData.application]);

  useEffect(() => {
    if (formData.application && !formData.assignedTo && filteredDevelopers.length === 1) {
      setFormData((prev) => ({ ...prev, assignedTo: filteredDevelopers[0]._id }));
    }
  }, [filteredDevelopers, formData.application, formData.assignedTo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const removeAttachment = (idToRemove) => {
    setAttachments(prev => prev.filter(att => (att.id || att._id) !== idToRemove));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.application) newErrors.application = 'Application is required';
    if (!formData.assignedTo) newErrors.assignedTo = 'Developer assignment is required';

    const selApp = applications.find(app => app._id === formData.application);
    if (selApp?.status === 'Inactive') {
      newErrors.application = `The application selected is inactive, you cannot log the ${workType.toLowerCase()}.`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill in mandatory fields');
      return;
    }

    setSubmitting(true);
    try {
      const ticketTypeString = workType.charAt(0).toUpperCase() + workType.slice(1);
      const cleanedTitle = (formData.title || '')
        .replace(/^(Tests?|Test)\s+(for|For)\s+(Bug|Observation|Improvement):\s*"?/i, '')
        .replace(/^(Bug|Observation|Improvement):\s*"?/i, '')
        .replace(/"?$/, '')
        .trim();
      const finalTitle = `${ticketTypeString}: ${cleanedTitle}`;

      // 1. Build a clean payload for the multipart/form-data request
      const formDataToSend = new FormData();
      
      const payload = {
        title: finalTitle,
        description: formData.description || '',
        application: formData.application || '',
        module: formData.module || '',
        category: formData.category === 'Other' ? (customCategory || 'Other') : formData.category,
        ticketType: ticketTypeString,
        severity: formData.severity || 'Medium',
        priority: formData.priority || 'Medium',
        assignedTo: formData.assignedTo || '',
        environment: formData.environment || '',
        domain: formData.domain || ''
      };

      // Append standard fields
      Object.entries(payload).forEach(([key, value]) => {
        formDataToSend.append(key, value || '');
      });

      // 2. Prepare attachments - strip frontend helper IDs before sending
      const cleanAttachments = attachments.map(att => {
        // eslint-disable-next-line no-unused-vars
        const { id, ...rest } = att; 
        return {
          ...rest,
          // If it doesn't have an _id, it's a new link/attachment
          _id: att._id || undefined 
        };
      });
      formDataToSend.append('existingAttachments', JSON.stringify(cleanAttachments));

      // 3. Append new files to upload
      selectedFiles.forEach(file => {
        formDataToSend.append('files', file);
      });

      await axios.put(`/api/bugs/${mongoId || id}`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Bug updated successfully!');
      navigate(`/bugs/${bugCode || id}`, { replace: true });
    } catch (error) {
      toast.error('Failed to update bug');
    } finally {
      setSubmitting(false);
    }
  };

  const getPreviewKind = (url) => {
    if (!url) return 'link';
    const clean = String(url || '').split('?')[0].toLowerCase();
    if (clean.match(/\.(png|jpg|jpeg|gif|webp|bmp|svg)$/)) return 'image';
    if (clean.endsWith('.pdf')) return 'pdf';
    if (clean.match(/\.(mp4|webm|ogg|mov|m4v)$/)) return 'video';
    return 'link';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className={`px-8 py-6 border-b border-gray-100 ${isObservation ? 'bg-amber-50' : isImprovement ? 'bg-emerald-50' : 'bg-slate-50/30'}`}>
            <h1 className="text-2xl font-bold text-gray-900">
              {workType === 'observation' ? 'Edit Observation' : 
               workType === 'improvement' ? 'Edit Improvement' : 
               'Edit Bug Report'}
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {bugCode || id} — Update established {workType === 'bug' ? 'bug' : workType} details
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={`w-full h-12 px-4 bg-gray-50 border rounded-lg focus:bg-white transition-all outline-none ${errors.title ? 'border-red-500 bg-red-50' : 'border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10'}`}
                  />
                  {errors.title && <p className="mt-2 text-xs text-red-500 font-medium">{errors.title}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Description</label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(html) => setFormData(prev => ({ ...prev, description: html }))}
                    minHeight="280px"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <PaperClipIcon className="h-4 w-4" /> Attachments
                  </label>
                  <div className="mb-4">
                    <AttachmentUploader files={selectedFiles} onChange={setSelectedFiles} />
                  </div>

                  <div className="grid grid-cols-1 gap-2">
                    {attachments.map(att => (
                      <div key={att.id || att._id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-blue-200 transition-colors">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">🔗</span>
                          <button
                            type="button"
                            onClick={() => setPreviewAttachment({ ...att, __previewUrl: att.url })}
                            className="text-sm font-bold text-blue-600 hover:underline decoration-2"
                          >
                            {att.title || att.filename || 'Attachment'}
                          </button>
                        </div>
                        <button type="button" onClick={() => removeAttachment(att.id || att._id)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors">✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 space-y-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Type</label>
                    <ScrollableDropdown
                      value={workType}
                      onChange={(val) => {
                        // Mark this as a user-initiated change so the useEffect
                        // resets severity/priority/category appropriately.
                        userChangedWorkType.current = true;
                        setWorkType(val);
                      }}
                      options={[
                        { value: 'bug', label: 'Bug Report' },
                        { value: 'observation', label: 'Observation' },
                        { value: 'improvement', label: 'Improvement' }
                      ]}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Application</label>
                    <div className="h-11 px-4 bg-gray-100 border border-gray-200 rounded-lg flex items-center text-sm font-bold text-gray-600">
                      {applications.find(a => a._id === formData.application)?.name || 'Loading...'}
                    </div>
                    {formData.application && applications.find(a => a._id === formData.application)?.status === 'Inactive' && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-100 rounded-lg">
                        <p className="text-xs font-bold text-red-700 flex items-center gap-2">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          Application Inactive
                        </p>
                        <p className="text-[11px] text-red-600 mt-1 font-medium italic">
                          The application selected is inactive, you cannot log the {workType.toLowerCase()}.
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Assign To *</label>
                    <ScrollableDropdown
                      value={formData.assignedTo}
                      onChange={(val) => handleChange({ target: { name: 'assignedTo', value: val } })}
                      options={filteredDevelopers.map(dev => ({ value: dev._id, label: dev.name }))}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Module</label>
                    <input
                      type="text"
                      name="module"
                      value={formData.module}
                      onChange={handleChange}
                      className="w-full h-11 px-4 bg-white border border-gray-200 rounded-lg outline-none focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Category</label>
                    <ScrollableDropdown
                      value={formData.category}
                      onChange={(val) => handleChange({ target: { name: 'category', value: val } })}
                      options={[
                        { value: 'Functional', label: 'Functional' },
                        { value: 'UI/UX', label: 'UI/UX' },
                        { value: 'Performance', label: 'Performance' },
                        { value: 'Security', label: 'Security' },
                        { value: 'Integration', label: 'Integration' },
                        { value: 'Data', label: 'Data' },
                        { value: 'Observation', label: 'Observation' },
                        { value: 'Other', label: 'Other' },
                      ]}
                    />
                    {formData.category === 'Other' && (
                      <input
                        type="text"
                        value={customCategory}
                        onChange={(e) => setCustomCategory(e.target.value)}
                        placeholder="Enter custom category"
                        className="w-full h-11 px-4 mt-2 bg-white border border-gray-200 rounded-lg outline-none focus:border-blue-500 text-sm"
                        autoFocus
                      />
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Severity</label>
                      <ScrollableDropdown
                        value={formData.severity}
                        onChange={(val) => handleChange({ target: { name: 'severity', value: val } })}
                        options={['Low', 'Medium', 'High', 'Critical'].map(v => ({ value: v, label: v }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Priority</label>
                      <ScrollableDropdown
                        value={formData.priority}
                        onChange={(val) => handleChange({ target: { name: 'priority', value: val } })}
                        options={['Low', 'Medium', 'High'].map(v => ({ value: v, label: v }))}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Environment</label>
                    <input
                      type="text"
                      name="environment"
                      value={formData.environment}
                      onChange={handleChange}
                      className="w-full h-11 px-4 bg-white border border-gray-200 rounded-lg outline-none focus:border-blue-500"
                    />
                  </div>

                  {domains.length > 0 && (
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Domain</label>
                      <ScrollableDropdown
                        value={formData.domain}
                        onChange={(val) => handleChange({ target: { name: 'domain', value: val } })}
                        options={domains.map(d => ({ value: d._id, label: d.name }))}
                        placeholder="Select Domain"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-100 flex items-center justify-between">
              <button
                type="button"
                onClick={() => navigate(`/bugs/${bugCode || id}`)}
                className="px-6 py-2.5 text-sm font-bold text-gray-600 hover:text-gray-900 transition-colors"
              >
                ← Back to Details
              </button>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => navigate(`/bugs/${bugCode || id}`)}
                  className="px-8 py-2.5 bg-gray-100 text-gray-700 text-sm font-bold rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || (formData.application && applications.find(app => app._id === formData.application)?.status === 'Inactive')}
                  className={`px-10 py-2.5 text-white text-sm font-black rounded-lg shadow-lg transition-transform active:scale-95 ${
                    isObservation ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/20' : 
                    isImprovement ? 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/20' : 
                    'bg-blue-600 hover:bg-blue-700 shadow-blue-500/20'}`}
                >
                  {submitting ? 'Updating...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Preview Modal */}
      {previewAttachment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setPreviewAttachment(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <span className="font-bold text-gray-900">{previewAttachment.title || 'Attachment Preview'}</span>
              <button onClick={() => setPreviewAttachment(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">✕</button>
            </div>
            <div className="flex-1 overflow-auto bg-gray-50 flex items-center justify-center">
              {['image', 'video'].includes(getPreviewKind(previewAttachment.__previewUrl)) ? (
                <div className="w-full p-4">
                  <MediaViewer src={previewAttachment.__previewUrl} kind={getPreviewKind(previewAttachment.__previewUrl)} title={previewAttachment.title} className={getPreviewKind(previewAttachment.__previewUrl) === 'image' ? 'h-[70vh]' : 'max-h-[70vh]'} />
                </div>
              ) : (
                <iframe src={previewAttachment.__previewUrl} title="Preview" className="w-full h-full border-none min-h-[70vh]"></iframe>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditBug;
